import React, { useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Card,
  Group,
  Center,
  Box,
  useMantineColorScheme,
  useMantineTheme,
  Loader
} from '@mantine/core';
import { IconX, IconArrowLeft, IconRefresh } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { mercadoPagoService } from '../services/mercadoPagoService';
import PageTransition from '../components/animations/PageTransition';

const PaymentFailurePage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);

  useEffect(() => {
    const processPayment = async () => {
      try {
        // Obtener información de la sesión de pago
        const paymentSession = mercadoPagoService.getPaymentSession();
        
        // Procesar resultado del pago
        const result = await mercadoPagoService.processPaymentResult(searchParams);
        
        setPaymentInfo({
          ...result,
          session: paymentSession
        });

        notifications.show({
          color: 'red',
          title: 'Pago no completado',
          message: result.message,
          autoClose: false
        });
        
      } catch (error) {
        console.error('Error procesando resultado de pago:', error);
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [searchParams]);

  const handleRetry = () => {
    const session = mercadoPagoService.getPaymentSession();
    mercadoPagoService.clearPaymentSession();
    
    if (session?.planId) {
      navigate('/mejorarplan');
    } else {
      navigate('/mejorarplan');
    }
  };

  const handleGoHome = () => {
    mercadoPagoService.clearPaymentSession();
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Center style={{ minHeight: '60vh' }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="lg">Procesando resultado del pago...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
    }}>
      <PageTransition type="medical" duration={600}>
        <Container size="md" py="xl">
          <Center style={{ minHeight: '70vh' }}>
            <Card
              shadow="md"
              radius="lg"
              p="xl"
              style={{
                maxWidth: 500,
                width: '100%',
                backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : 'white',
                border: `1px solid ${colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`
              }}
            >
              <Stack align="center" gap="lg">
                {/* Icono de error */}
                <Box
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: theme.colors.red[6],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}
                >
                  <IconX size={40} color="white" />
                </Box>
                
                <Stack align="center" gap="xs">
                  <Title 
                    order={2} 
                    ta="center"
                    c={colorScheme === 'dark' ? 'white' : 'dark'}
                  >
                    Pago no completado
                  </Title>
                  
                  <Text 
                    size="md" 
                    ta="center" 
                    c={colorScheme === 'dark' ? 'gray.3' : 'gray.7'}
                  >
                    {paymentInfo?.message || 'No se pudo procesar tu pago. Esto puede deberse a fondos insuficientes, datos incorreos o problemas técnicos.'}
                  </Text>
                </Stack>

                {/* Información del pago fallido */}
                {paymentInfo?.session && (
                  <Card
                    radius="md"
                    p="md"
                    style={{
                      backgroundColor: colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
                      width: '100%'
                    }}
                  >
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Plan intentado:</Text>
                        <Text size="sm" fw={600}>{paymentInfo.session.planName}</Text>
                      </Group>
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Monto:</Text>
                        <Text size="sm" fw={600}>${paymentInfo.session.amount}</Text>
                      </Group>

                      {paymentInfo.paymentId && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">ID de transacción:</Text>
                          <Text size="sm" ff="monospace">{paymentInfo.paymentId}</Text>
                        </Group>
                      )}
                    </Stack>
                  </Card>
                )}

                {/* Sugerencias */}
                <Card
                  radius="md"
                  p="md"
                  style={{
                    backgroundColor: colorScheme === 'dark' ? theme.colors.yellow[9] + '20' : theme.colors.yellow[0],
                    border: `1px solid ${theme.colors.yellow[colorScheme === 'dark' ? 7 : 3]}`,
                    width: '100%'
                  }}
                >
                  <Stack gap="xs">
                    <Text size="sm" fw={600} c={colorScheme === 'dark' ? 'yellow.4' : 'yellow.8'}>
                      Sugerencias para completar tu pago:
                    </Text>
                    <Text size="sm" c={colorScheme === 'dark' ? 'gray.3' : 'gray.7'}>
                      • Verifica que tu tarjeta tenga fondos suficientes<br/>
                      • Confirma que los datos de la tarjeta sean correctos<br/>
                      • Intenta con otro método de pago<br/>
                      • Contacta a tu banco si el problema persiste
                    </Text>
                  </Stack>
                </Card>

                {/* Botones de acción */}
                <Group justify="center" gap="md" style={{ width: '100%' }}>
                  <Button
                    variant="light"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={handleGoHome}
                    style={{ flex: 1 }}
                  >
                    Ir al inicio
                  </Button>
                  <Button
                    leftSection={<IconRefresh size={16} />}
                    onClick={handleRetry}
                    style={{ flex: 1 }}
                  >
                    Intentar de nuevo
                  </Button>
                </Group>

                <Text size="xs" ta="center" c="dimmed">
                  Si necesitas ayuda, contacta a nuestro soporte técnico
                </Text>
              </Stack>
            </Card>
          </Center>
        </Container>
      </PageTransition>
    </div>
  );
};

export default PaymentFailurePage;